import { Menu } from 'lucide-react';
import React, { useMemo, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { RouteNavigationProgress } from '../../../components/navigation/RouteNavigationProgress';
import { ThemeToggle } from '../../../forge/components/layout/ThemeToggle';
import { useForgeTheme } from '../../../forge/hooks/useForgeTheme';
import { useMobileNavDrawer } from '../../../forge/hooks/useMobileNavDrawer';
import { useSidebar } from '../../../forge/hooks/useSidebar';
import '../../../forge/styles/forge-theme.css';
import { UiProvider } from '../../../ui/ui-provider';
import '../../styles/eval-theme.css';
import { getEvalPageLabel } from './evalNavigation';
import { EvalSidebar } from './EvalSidebar';

export const EvalLayout: React.FC = () => {
  const { theme, toggleTheme } = useForgeTheme();
  const { width, isCollapsed, isResizing, toggleCollapse, onDragStart } = useSidebar();
  const location = useLocation();
  const {
    isMobile,
    isOpen: isMobileSidebarOpen,
    open: openMobileSidebar,
    close: closeMobileSidebar,
    openerRef: mobileNavButtonRef,
    drawerRef: mobileSidebarShellRef,
  } = useMobileNavDrawer(location.pathname);
  const [portalContainer, setPortalContainer] = useState<HTMLDivElement | null>(null);
  const pageLabel = useMemo(() => getEvalPageLabel(location.pathname), [location.pathname]);

  return (
    <div className="forge-root forge-shell" data-forge-theme={theme} ref={setPortalContainer}>
      <UiProvider container={portalContainer}>
        <RouteNavigationProgress />
        <div className={`forge-layout ${isMobile ? 'is-mobile-shell' : ''}`}>
          {isMobile ? (
            <div className="forge-mobile-topbar">
              <button
                type="button"
                ref={mobileNavButtonRef}
                className="forge-mobile-nav-button"
                aria-label="Open navigation"
                aria-expanded={isMobileSidebarOpen}
                onClick={openMobileSidebar}
              >
                <Menu />
              </button>
              <div className="forge-mobile-title">{pageLabel}</div>
              <ThemeToggle theme={theme} onToggle={toggleTheme} variant="topbar" />
            </div>
          ) : null}
          <div
            ref={mobileSidebarShellRef}
            tabIndex={-1}
            className={`forge-sidebar-shell ${isMobileSidebarOpen ? 'is-mobile-open' : ''}`}
          >
            {isMobile ? (
              <button
                type="button"
                className="forge-sidebar-backdrop"
                aria-label="Close navigation"
                onClick={closeMobileSidebar}
              />
            ) : null}
            <EvalSidebar
              theme={theme}
              width={width}
              isCollapsed={isCollapsed}
              isResizing={isResizing}
              toggleCollapse={toggleCollapse}
              onDragStart={onDragStart}
              toggleTheme={toggleTheme}
              currentPath={location.pathname}
              isMobile={isMobile}
              onMobileNavigate={isMobile ? closeMobileSidebar : undefined}
            />
          </div>
          <main
            className={`forge-main-content ${isResizing ? 'sidebar-resizing' : ''}`}
            style={{ marginLeft: isMobile ? 0 : isCollapsed ? 44 : width }}
          >
            <Outlet />
          </main>
        </div>
      </UiProvider>
    </div>
  );
};
